import { useState, useEffect } from "react";
import { Search, Calendar, Package, Clock, XCircle, CheckCircle, Info, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getPickups } from "@/services/api";
import { toast } from "sonner";

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'requested':
    case 'pending': return "#38bdf8";
    case 'assigned': return "#f97316";
    case 'reached': return "#0a2c99";
    case 'collected':
    case 'completed':
    case 'disposed': return "#07993d";
    case 'cancelled':
    case 'canceld':
    case 'missed': return "#ef4444";
    default: return "#6b7280";
  }
};

const DailyCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [mainTab, setMainTab] = useState("one-time-pickups");

  useEffect(() => {
    const today = new Date();
    const offset = today.getTimezoneOffset() * 60000;
    setSelectedDate(new Date(today.getTime() - offset).toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchCollections();
    }
  }, [selectedDate]);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const data = await getPickups(1000, null, selectedDate);
      if (data && data.success) {
        setCollections(data.pickups || []);
      }
    } catch (error) {
      console.error("Error fetching daily collections:", error);
      toast.error("Failed to load daily pickups.");
    } finally {
      setLoading(false);
    }
  };

  const filteredCollections = collections.filter(c => {
    const isRecurring = c.pickup_type === 'recurring';
    if (mainTab === 'one-time-pickups' && isRecurring) return false;
    if (mainTab === 'recurring' && !isRecurring) return false;

    const matchesSearch = 
      c.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.worker?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.captain_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.remarks?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let normalizedStatus = c.status?.toLowerCase();
    if (normalizedStatus === "requested") normalizedStatus = "pending";
    if (normalizedStatus === "assigned" || normalizedStatus === "collected" || normalizedStatus === "accepted") normalizedStatus = "in-progress";
    if (normalizedStatus === "disposed" || normalizedStatus === "completed") normalizedStatus = "completed";

    const matchesStatus = filterStatus === "all" || 
      (filterStatus === "collected" && ["collected", "completed", "disposed"].includes(c.status)) ||
      (filterStatus === "missed" && ["missed", "requested", "assigned", "accepted" ,"reached"].includes(c.status)) ||
      (filterStatus === "cancelled" && ["cancelled"].includes(c.status)) ||
      (filterStatus === "pending" && ["pending", "requested"].includes(c.status)) ||
      (filterStatus === "in-progress" && ["assigned", "collected", "accepted", "reached"].includes(c.status));
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: collections.length,
    covered: collections.filter(c => ["collected", "completed", "disposed"].includes(c.status)).length,
    missed: collections.filter(c => ["missed", "requested", "assigned", "accepted","reached"].includes(c.status)).length,
    cancelled: collections.filter(c => ["cancelled"].includes(c.status)).length,
  };

  const handlePrevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Pickup Management</h1>
          <p className="text-muted-foreground mt-1">View all pickup records.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-card p-2 rounded-lg border shadow-sm">
            <Button variant="ghost" size="icon" onClick={handlePrevDay} className="h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Calendar className="w-5 h-5 text-primary ml-1" />
            <Input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border-none focus-visible:ring-0 w-36 px-2"
            />
            <Button variant="ghost" size="icon" onClick={handleNextDay} className="h-8 w-8">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card 
          className={`border-border cursor-pointer hover:shadow-md transition-all ${filterStatus === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus("all")}
        >
          <CardContent className="p-6 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Pickups</p>
                <h3 className="text-2xl font-bold mt-1">{stats.total}</h3>
              </div>
              <div className="p-3 bg-primary/10 rounded-full"><Package className="w-6 h-6 text-primary" /></div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`border-border cursor-pointer hover:shadow-md transition-all ${filterStatus === 'collected' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus("collected")}
        >
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Completed</p>
              <h3 className="text-2xl font-bold mt-1 text-green-600">{stats.covered}</h3>
            </div>
            <div className="p-3 bg-green-100 rounded-full"><CheckCircle className="w-6 h-6 text-green-600" /></div>
          </CardContent>
        </Card>

        <Card 
          className={`border-border cursor-pointer hover:shadow-md transition-all ${filterStatus === 'missed' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus("missed")}
        >
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Missed</p>
              <h3 className="text-2xl font-bold mt-1 text-orange-500">{stats.missed}</h3>
            </div>
            <div className="p-3 bg-orange-100 rounded-full"><XCircle className="w-6 h-6 text-orange-500" /></div>
          </CardContent>
        </Card>

        <Card 
          className={`border-border cursor-pointer hover:shadow-md transition-all ${filterStatus === 'cancelled' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilterStatus("cancelled")}
        >
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Cancelled</p>
              <h3 className="text-2xl font-bold mt-1 text-destructive">{stats.cancelled}</h3>
            </div>
            <div className="p-3 bg-red-100 rounded-full"><XCircle className="w-6 h-6 text-destructive" /></div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="text-xl">
              Pickup Records for {new Date(selectedDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search user, captain, remarks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  className="pl-10" 
                />
              </div>
              <Button variant="outline" onClick={fetchCollections} className="gap-2">
                <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
                <TabsTrigger value="one-time-pickups">One-time Pickups</TabsTrigger>
                <TabsTrigger value="recurring">Recurring Pickups</TabsTrigger>
              </TabsList>
            </div>
            
            <div className="rounded-md border overflow-hidden">
              <TabsContent value="one-time-pickups" className="m-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-bold w-[25%]">Customer Name</TableHead>
                      <TableHead className="font-bold w-[20%]">Captain Name</TableHead>
                      <TableHead className="font-bold text-center">Status</TableHead>
                      <TableHead className="font-bold text-center">Source</TableHead>
                      <TableHead className="font-bold">Remarks</TableHead>
                      <TableHead className="font-bold text-right">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            <span className="text-muted-foreground">Loading pickups data...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredCollections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                          <div className="flex flex-col items-center gap-2 opacity-50">
                            <Info className="w-8 h-8" />
                            <span>No pickups found for this date.</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCollections.map((record) => {
                        const userName = record.user?.name || record.user_name || "Unknown";
                        const displayStatus = record.status;
                        const source = record.createdByAdmin ? "Admin" : "App";
                        const captainName = record.status !== "requested" ? (record.worker_name || record.worker?.name || record.captain_name || record.agent_name || "Unassigned") : "Not Assigned";
                        let remarks = "";
                        if (record.remarks && record.remarks.trim() !== "") {
                          remarks = record.remarks;
                        } else {
                          if (record.status === "collected") {
                            const actualWorker = record.worker_name || record.worker?.name || record.captain_name || record.agent_name;
                            remarks = actualWorker ? ` collected by captain ` : " collected by admin";
                          } else if (record.status === "requested") {
                            remarks = source === "Admin" ? "Requested by admin" : "Requested by user";
                          } else if (record.status === "cancelled") {
                            remarks = "Cancelled by admin";
                          } else {
                            remarks ="No remarks";
                          }
                        }

                        return (
                          <TableRow key={record._id || record.id} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-medium text-foreground">
                              {userName}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                                  {captainName[0]?.toUpperCase()}
                                </div>
                                <span className="text-sm">{captainName}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div 
                                className="capitalize px-3 py-1 text-xs font-semibold rounded-full text-white inline-block"
                                style={{ backgroundColor: getStatusColor(record.status) }}
                              >
                                {displayStatus}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="capitalize">
                                {source}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <p className="text-sm italic text-muted-foreground truncate" title={remarks}>
                                {remarks}
                              </p>
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground font-mono">
                              {new Date(record.created_at || record.createdAt || record.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
              
              <TabsContent value="recurring" className="m-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-bold w-[25%]">Customer Name</TableHead>
                      <TableHead className="font-bold">Captain Name</TableHead>
                      <TableHead className="font-bold text-center">Status</TableHead>
                      <TableHead className="font-bold text-center">Source</TableHead>
                      <TableHead className="font-bold">Remarks</TableHead>
                      <TableHead className="font-bold text-right">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            <span className="text-muted-foreground">Loading pickups data...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredCollections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                          <div className="flex flex-col items-center gap-2 opacity-50">
                            <Info className="w-8 h-8" />
                            <span>No recurring pickups found.</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCollections.map((record) => {
                        const userName = record.user?.name || record.user_name || "Unknown";
                        const displayStatus = record.status;
                        const source = record.createdByAdmin ? "Admin" : "App";
                        const captainName = record.status !== "requested" ? (record.worker_name || record.worker?.name || record.captain_name || record.agent_name || "Unassigned") : "Not Assigned";
                        let remarks = "";
                        if (record.remarks && record.remarks.trim() !== "") {
                          remarks = record.remarks;
                        } else {
                          if (record.status === "collected") {
                            const actualWorker = record.worker_name || record.worker?.name || record.captain_name || record.agent_name;
                            remarks = actualWorker ? ` collected by captain ` : " collected by admin";
                          } else if (record.status === "requested") {
                            remarks = source === "Admin" ? "Requested by admin" : "Requested by user";
                          } else if (record.status === "cancelled") {
                            remarks = "Cancelled by admin";
                          } else {
                            remarks ="No remarks";
                          }
                        }

                        return (
                          <TableRow key={record._id || record.id} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="font-medium text-foreground">
                              {userName}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                                  {captainName[0]?.toUpperCase()}
                                </div>
                                <span className="text-sm">{captainName}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div 
                                className="capitalize px-3 py-1 text-xs font-semibold rounded-full text-white inline-block"
                                style={{ backgroundColor: getStatusColor(record.status) }}
                              >
                                {displayStatus}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="capitalize">
                                {source}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <p className="text-sm italic text-muted-foreground truncate" title={remarks}>
                                {remarks}
                              </p>
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground font-mono">
                              {new Date(record.created_at || record.createdAt || record.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyCollections;